import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, Globe2, Hourglass } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";

/* =============================================================
   Tarifs Sama Écoute
   Les prix Premium et la bannière promotionnelle sont gérés
   depuis le panneau SuperAdmin (/admin/parametres) — pas ici.
   ============================================================= */

const ZONES = {
  afrique: { label: "🌍 Sénégal & Afrique", currency: "FCFA", format: "fr-FR", currencyPosition: "suffix", settingsKey: "tarif_price_afrique", fallback: 5500 },
  europe:  { label: "🇪🇺 Europe", currency: "€", format: "fr-FR", currencyPosition: "suffix", settingsKey: "tarif_price_europe", fallback: 15 },
  usd:     { label: "🌎 Amérique — USD", currency: "$ USD", format: "en-US", currencyPosition: "prefix", settingsKey: "tarif_price_usd", fallback: 18 },
  cad:     { label: "🌎 Amérique — CAD", currency: "$ CAD", format: "fr-CA", currencyPosition: "prefix", settingsKey: "tarif_price_cad", fallback: 24 },
};

const DEFAULT_ZONE = "afrique";

const FREE_FEATURES = [
  "1 séance de discussion de 15 min",
];

const PREMIUM_FEATURES = [
  "Séance individuelle complète",
  "Écoute active & accompagnement personnalisé",
  "Confidentialité garantie",
  "Visioconférence, appel audio ou messagerie",
];

function formatPrice(zoneKey, settings) {
  const z = ZONES[zoneKey];
  if (!z) return "";
  const amount = Number(settings?.[z.settingsKey] ?? z.fallback);
  const num = new Intl.NumberFormat(z.format).format(amount);
  if (z.currencyPosition === "prefix") return `${z.currency} ${num}`;
  return `${num} ${z.currency}`;
}

export default function Tarifs() {
  const { settings } = useSettings();
  const { user } = useAuth();
  const [zone, setZone] = useState(DEFAULT_ZONE);
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const tarifsVisible = settings?.tarifs_visible === true;

  // Gating : si tarifs désactivés et l'utilisateur n'est pas admin, afficher un placeholder
  if (!tarifsVisible && !isAdmin) {
    return (
      <div className="container-jamm py-24" data-testid="tarifs-disabled-page">
        <div className="max-w-xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-beige border border-gold/40 mb-6">
            <Hourglass className="w-7 h-7 text-gold" />
          </div>
          <div className="overline">Bientôt disponible</div>
          <h1 className="h-section mt-4">Les tarifs arrivent très bientôt.</h1>
          <p className="lead mt-4">
            Nous finalisons notre grille tarifaire pour vous offrir l&apos;expérience la plus juste. En attendant, vous pouvez nous écrire pour toute question.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/" className="btn-outline" data-testid="tarifs-back-home">Retour à l&apos;accueil</Link>
            <Link to="/#contact" className="btn-primary" data-testid="tarifs-go-contact">Nous contacter</Link>
          </div>
        </div>
      </div>
    );
  }

  const promoActive = settings?.promo_active !== false;
  const promoAmount = (settings?.promo_amount || "").trim();
  const showBanner = promoActive && promoAmount.length > 0;

  return (
    <div className="container-jamm py-16">
      {!tarifsVisible && isAdmin && (
        <div className="mb-6 rounded-2xl border border-gold/50 bg-gold/10 px-5 py-4 text-sm text-ink-soft flex items-start gap-3" data-testid="tarifs-admin-preview-notice">
          <Hourglass className="w-5 h-5 text-gold shrink-0 mt-0.5" />
          <div>
            <strong className="text-ink">Aperçu admin uniquement.</strong> La page Tarifs est actuellement <strong>masquée</strong> pour les visiteurs et clients. Vous pouvez la réactiver depuis <Link to="/admin/parametres" className="text-sage-600 underline hover:text-sage-700">Paramètres → Gestion des Tarifs</Link>.
          </div>
        </div>
      )}
      <div className="text-center max-w-2xl mx-auto">
        <div className="overline">Tarifs</div>
        <h1 className="h-display mt-3">Choisissez ce qui vous <span className="italic text-sage-600">ressemble</span>.</h1>
        <p className="lead mt-4">Deux formules. Pas d&apos;engagement. Pas de surprise.</p>
      </div>

      {/* Bannière promotionnelle */}
      {showBanner && (
        <div
          className="mt-10 max-w-3xl mx-auto rounded-2xl px-6 py-4 flex items-center justify-center gap-3 text-center shadow-md"
          style={{ background: "linear-gradient(135deg, #f5e0a8 0%, #C2A665 100%)", color: "#1A2421" }}
          data-testid="tarif-promo-banner"
        >
          <Sparkles className="w-5 h-5 shrink-0" strokeWidth={2} />
          <div className="text-sm sm:text-base font-medium">
            🎉 <span className="font-semibold">Tarif promotionnel</span> : {promoAmount} — <span className="italic">Offre limitée !</span>
          </div>
        </div>
      )}

      {/* Sélecteur de zone géographique */}
      <div className="mt-10 max-w-md mx-auto">
        <label htmlFor="zone-select" className="block text-sm text-ink-soft mb-2 text-center">
          <Globe2 className="inline w-4 h-4 mr-1 -mt-0.5" />
          Votre zone géographique — le prix Premium s&apos;adapte
        </label>
        <select
          id="zone-select"
          value={zone}
          onChange={(e) => setZone(e.target.value)}
          className="w-full rounded-xl border border-border bg-white px-4 py-3 text-ink focus:outline-none focus:ring-2 focus:ring-sage-300 transition shadow-sm"
          data-testid="tarif-zone-select"
        >
          {Object.entries(ZONES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {/* Cartes tarifs */}
      <div className="grid md:grid-cols-2 gap-6 mt-10 max-w-4xl mx-auto">
        {/* Plan Gratuit */}
        <div className="card-soft card-soft-hover relative" data-testid="tarif-plan-free">
          <div className="font-display text-3xl text-ink">Gratuit</div>
          <div className="mt-2 text-ink-soft text-sm">Toujours gratuit</div>
          <div className="mt-5">
            <span className="font-display text-5xl text-ink">0</span>
          </div>
          <ul className="mt-6 space-y-3">
            {FREE_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-ink-soft text-sm">
                <Check className="w-4 h-4 text-sage-500 mt-0.5 shrink-0" />{f}
              </li>
            ))}
          </ul>
          <Link to="/register" className="mt-7 inline-flex w-full justify-center btn-outline" data-testid="tarif-cta-free">
            Commencer
          </Link>
        </div>

        {/* Plan Premium */}
        <div className="card-soft card-soft-hover relative border-2 border-gold ring-1 ring-gold/30" data-testid="tarif-plan-premium">
          <span className="absolute -top-3 left-6 text-[10px] uppercase tracking-[0.2em] bg-gold text-ink px-3 py-1 rounded-full font-semibold">
            Recommandé
          </span>
          <div className="font-display text-3xl text-ink">Premium</div>
          <div className="mt-2 text-ink-soft text-sm">Par séance</div>
          <div className="mt-5">
            <span className="font-display text-5xl text-ink" data-testid="tarif-premium-price">{formatPrice(zone, settings)}</span>
          </div>
          <ul className="mt-6 space-y-3">
            {PREMIUM_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-ink-soft text-sm">
                <Check className="w-4 h-4 text-sage-500 mt-0.5 shrink-0" />{f}
              </li>
            ))}
          </ul>
          <Link to="/accompagnateurs" className="mt-7 inline-flex w-full justify-center btn-secondary" data-testid="tarif-cta-premium">
            Réserver une séance
          </Link>
        </div>
      </div>

      <div className="mt-20 max-w-3xl mx-auto card-soft text-center">
        <div className="overline">Bon à savoir</div>
        <h2 className="font-display text-3xl mt-3 text-ink">Vous payez les séances séparément.</h2>
        <p className="lead mt-3">Chaque séance individuelle reste libre — vous payez seulement ce que vous consommez, sans abonnement.</p>
      </div>
    </div>
  );
}
